import React, { useCallback, useEffect } from "react";

import CONSTANTS from "../../../misc/constants";

import CloudinaryMediaLibrary from "../../Cloudinary/CloudinaryMediaLibrary";
import MultiInput from "../../utils/MultiInput";
import RichTextEditor from "../../utils/RichTextEditor";

import Text from "../../utils/Text";
import Select from "../../utils/Select";
import Check from "../../utils/Check";
import { useRef } from "react";

const EntryForm = ({ onSubmit, states, setStates, action, initialValues }) => {
  const firstRender = useRef(true);

  const {
    featuredImage,
    images,
    tags,
    videos,
    importantLinks,
    description,
    text,
    entryType,
    title,
    slug,
    featured,
    modelURL,
    lang,
    prevLang,
  } = states;

  const {
    setFeaturedImage,
    setImages,
    setTags,
    setVideos,
    setImportantLinks,
    setDescription,
    setText,
    setEntryType,
    setTitle,
    setSlug,
    setFeatured,
    setModelURL,
    setCreationDate,
    setLang,
    setPrevLang,
  } = setStates;

  const populateForm = useCallback(() => {
    if (action === "edit" && initialValues && initialValues.length === 1) {
      const entry = initialValues[0].value;

      setEntryType(entry.entryType);
      setSlug(entry.slug);

      if (entry.content && entry.content[lang]) {
        setTitle(entry.content[lang].title || "");
        setModelURL(entry.content[lang].model_URL || "");
        setFeaturedImage(
          entry.content[lang].featured_image
            ? entry.content[lang].featured_image
            : null
        );
        setImages(entry.content[lang].images || []);
        setVideos(entry.content[lang].videos || []);
        setImportantLinks(entry.content[lang].important_links || []);
        setDescription(entry.content[lang].description || "");
        setText(entry.content[lang].text || "");
      } else {
        console.log("a");
        setTitle("");
        setModelURL("");
        setFeaturedImage(null);
        setImages([]);
        setVideos([]);
        setImportantLinks([]);
        setDescription("");
        setText("");
      }

      setFeatured(entry.meta.featured || false);

      if (entry.meta.tags) {
        setTags(Object.keys(entry.meta.tags));
      } else {
        setTags([]);
      }

      if (entry.meta.creationDate) {
        setCreationDate(entry.meta.creationDate);
      }
      firstRender.current = false;
    }
  }, [
    initialValues,
    action,
    lang,
    setFeaturedImage,
    setImages,
    setTags,
    setVideos,
    setImportantLinks,
    setDescription,
    setText,
    setEntryType,
    setTitle,
    setSlug,
    setFeatured,
    setModelURL,
    setCreationDate,
  ]);

  useEffect(() => {
    if (firstRender.current) {
      populateForm();
    }
    if (prevLang !== lang) {
      setPrevLang(lang);
      populateForm();
    }
    return ()=>{
      firstRender.current = true;
    }
  }, [populateForm, lang, prevLang, setPrevLang]);

  const handleTitle = (e) => {
    setTitle(e);
    if (action !== "edit") {
      setSlug(e.toLowerCase().replace(/[ ]/g, "_"));
    }
  };

  return (
    <>
      <form onSubmit={onSubmit}>
        <label htmlFor="entry-type-select">Entry Type</label>
        <div className="input-field">
          <Select
            value={entryType}
            onChange={setEntryType}
            options={[
              { label: "Basic", value: CONSTANTS.ENTRY_TYPES.BASIC },
              {
                label: "3d Modeling",
                value: CONSTANTS.ENTRY_TYPES.MODELING_3D,
              },
              {
                label: "Web Development",
                value: CONSTANTS.ENTRY_TYPES.WEB_DEVELOPMENT,
              },

              {
                label: "Game Development",
                value: CONSTANTS.ENTRY_TYPES.GAME_DEVELOPMENT,
              },
              { label: "Pixel Art", value: CONSTANTS.ENTRY_TYPES.PIXEL_ART },
              { label: "Writing", value: CONSTANTS.ENTRY_TYPES.WRITING },
            ]}
            name="entry-type-select"
          />
        </div>
        <div className="input-field">
          <Select
            value={lang}
            onChange={setLang}
            options={[
              { label: "Español", value: "esp" },
              {
                label: "English",
                value: "en",
              },
            ]}
            name="entry-lang-select"
          />
        </div>

        <Text
          id="title"
          className="input"
          name="title"
          placeholder="Title"
          value={title}
          onChange={handleTitle}
          required={true}
        />

        <Text
          id="slug"
          className="input"
          name="slug"
          placeholder="Slug"
          value={slug}
          onChange={setSlug}
          required={true}
        />

        <Check
          label="Featured entry?"
          name="featured"
          id="featured"
          value={featured}
          onChange={setFeatured}
        />

        <RichTextEditor
          placeholder="Description"
          value={description}
          onChange={setDescription}
        />

        <div className="row">
          <div className="column column-25">
            <fieldset>
              <legend>Featured Image</legend>
              <CloudinaryMediaLibrary
                value={featuredImage}
                onChange={setFeaturedImage}
                preview={true}
                id="featured-image-select"
              >
                Set/Upload
              </CloudinaryMediaLibrary>
            </fieldset>
          </div>
          <div className="column column-75">
            <fieldset>
              <legend>Images</legend>
              <CloudinaryMediaLibrary
                value={images}
                onChange={setImages}
                id="featured-image-select"
                multiple={true}
                preview={true}
              >
                Images
              </CloudinaryMediaLibrary>
            </fieldset>
          </div>
        </div>

        <fieldset>
          <legend>Videos</legend>
          <MultiInput Wrapper={"a"} values={videos} onChange={setVideos} />
        </fieldset>

        <fieldset>
          <legend>Tags</legend>
          <MultiInput keyName="tags" values={tags} onChange={setTags} />
        </fieldset>

        <fieldset>
          <legend>Important Links</legend>
          <MultiInput
            Wrapper={"a"}
            regex={CONSTANTS.REGEX.LINK}
            values={importantLinks}
            onChange={setImportantLinks}
          />
        </fieldset>

        {entryType === CONSTANTS.ENTRY_TYPES.MODELING_3D && (
          <>
            <fieldset>
              <legend>3D Fields</legend>
              <Text
                name="modelURL"
                id="modelURL"
                type="url"
                placeholder="Sketchfab model url"
                value={modelURL}
                onChange={setModelURL}
              />
            </fieldset>
          </>
        )}

        {entryType === CONSTANTS.ENTRY_TYPES.WRITING && (
          <fieldset>
            <legend>Writing Fields</legend>
            <RichTextEditor
              id="writing-text"
              placeholder="Text"
              value={text}
              onChange={setText}
            />
          </fieldset>
        )}

        <input
          type="submit"
          value={action === "edit" ? "Update" : "Create"}
          className="btn"
        />
      </form>
    </>
  );
};

export default EntryForm;
